/**
 * AdminLtiCard — Super Admin › LTI 1.3 Tool Provider
 *
 * Register LMS platforms (Canvas / Moodle / Blackboard) as issuers,
 * add deployments under each, and view the tool-config a Platform admin
 * needs to import into their LMS.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, PlusCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminLtiCard() {
    const [items, setItems] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        iss: "https://canvas.instructure.com",
        client_id: "",
        keyset_url: "https://canvas.instructure.com/api/lti/security/jwks",
        auth_login_url: "https://canvas.instructure.com/api/lti/authorize_redirect",
        auth_token_url: "https://canvas.instructure.com/login/oauth2/token",
        display_name: "Canvas Cloud",
    });
    const [depForm, setDepForm] = useState({ iss: "", deployment_id: "", label: "" });

    async function load() {
        setBusy(true);
        try {
            const [inst, dep] = await Promise.all([
                api.get("/admin/lti/institutions"),
                api.get("/admin/lti/deployments"),
            ]);
            setItems(inst.data?.items || []);
            setDeployments(dep.data?.items || []);
        } catch (e) {
            toast.error("Failed to load LTI registry");
        } finally { setBusy(false); }
    }

    useEffect(() => { load(); }, []);

    async function submitInstitution(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/lti/institutions", form);
            toast.success("Institution registered");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to register");
        } finally { setBusy(false); }
    }

    async function deleteInstitution(id) {
        if (!window.confirm("Remove this institution and all its deployments?")) return;
        setBusy(true);
        try {
            await api.delete(`/admin/lti/institutions/${id}`);
            toast.success("Removed");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        } finally { setBusy(false); }
    }

    async function addDeployment(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/lti/deployments", depForm);
            toast.success("Deployment added");
            setDepForm({ iss: depForm.iss, deployment_id: "", label: "" });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add deployment");
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-4" data-testid="admin-lti-card">
            <div className="border border-border bg-card rounded-xl p-4">
                <div className="overline mb-2">LTI 1.3 Tool Provider</div>
                <p className="text-xs text-muted-foreground mb-3">
                    Register any LMS Platform (Canvas, Moodle, Blackboard, D2L)
                    that will bolt Melian into its courses. Once added, share
                    the Tool config with the Platform admin:
                </p>
                <div className="text-xs mono bg-muted/40 rounded p-2 flex items-center gap-2 mb-2">
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    <a
                        href={`${window.location.origin.replace(/\/$/, "")}/api/lti/config`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline"
                        data-testid="lti-config-url"
                    >
                        /api/lti/config
                    </a>
                    <span className="text-muted-foreground">
                        · JWKS at <span className="text-foreground">/api/lti/jwks</span>
                    </span>
                </div>
            </div>

            <form onSubmit={submitInstitution} className="border border-border bg-card rounded-xl p-4 space-y-3">
                <div className="overline">Register a Platform</div>
                {["iss", "client_id", "keyset_url", "auth_login_url", "auth_token_url", "display_name"].map((k) => (
                    <div key={k}>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">{k}</label>
                        <Input
                            value={form[k] || ""}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            data-testid={`lti-inst-${k}`}
                            required={k !== "display_name"}
                        />
                    </div>
                ))}
                <Button type="submit" disabled={busy} data-testid="lti-inst-submit">
                    <PlusCircle className="w-4 h-4 mr-1" /> Register institution
                </Button>
            </form>

            <div className="border border-border bg-card rounded-xl p-4">
                <div className="overline mb-2">Registered institutions</div>
                {items.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No LMS platforms registered yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground">
                            <tr>
                                <th className="text-left px-2 py-1">Issuer</th>
                                <th className="text-left px-2 py-1">client_id</th>
                                <th className="text-left px-2 py-1">Deployments</th>
                                <th className="text-right px-2 py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((i) => {
                                const iCount = deployments.filter((d) => d.iss === i.iss).length;
                                return (
                                    <tr key={i._id} className="border-b border-border/40" data-testid={`lti-row-${i._id}`}>
                                        <td className="px-2 py-2 mono text-xs">{i.iss}</td>
                                        <td className="px-2 py-2 mono text-xs">{i.client_id}</td>
                                        <td className="px-2 py-2 text-xs">{iCount}</td>
                                        <td className="px-2 py-2 text-right">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => deleteInstitution(i._id)}
                                                data-testid={`lti-inst-del-${i._id}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <form onSubmit={addDeployment} className="border border-border bg-card rounded-xl p-4 space-y-3">
                <div className="overline">Add a deployment</div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Issuer</label>
                    <select
                        className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
                        value={depForm.iss}
                        onChange={(e) => setDepForm({ ...depForm, iss: e.target.value })}
                        data-testid="lti-dep-iss"
                    >
                        <option value="">Select…</option>
                        {items.map((i) => (
                            <option key={i._id} value={i.iss}>{i.display_name || i.iss}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">deployment_id</label>
                    <Input
                        value={depForm.deployment_id}
                        onChange={(e) => setDepForm({ ...depForm, deployment_id: e.target.value })}
                        data-testid="lti-dep-id"
                        required
                    />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Label (optional)</label>
                    <Input
                        value={depForm.label}
                        onChange={(e) => setDepForm({ ...depForm, label: e.target.value })}
                        data-testid="lti-dep-label"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={busy || !depForm.iss || !depForm.deployment_id}
                    data-testid="lti-dep-submit"
                >
                    <PlusCircle className="w-4 h-4 mr-1" /> Add deployment
                </Button>
            </form>
        </div>
    );
}
